import { useMemo } from "react";

const PALETTE = ["#FF3B30", "#0A84FF", "#19E3FF", "#FFD60A", "#30D158", "#FF9F0A", "#FF2D55", "#FFFFFF"];

// Full-screen "grand" scoreboard slide shown between media items.
export function Scoreboard({ data }) {
  if (!data) return null;
  const matches = [...(data.matches || [])].sort((a, b) => {
    const rank = (s) => (s === "in" ? 0 : s === "pre" ? 1 : 2);
    return rank(a.state) - rank(b.state);
  }).slice(0, 3);

  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center text-white overflow-hidden"
      data-testid="player-scoreboard"
      style={{
        background:
          "radial-gradient(ellipse at 50% 0%, #0b2a52 0%, #05153a 45%, #030a1f 100%)",
      }}
    >
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(#19E3FF 1px, transparent 1px)", backgroundSize: "44px 44px" }}
      />

      {/* Header */}
      <div className="relative flex items-center gap-4 mb-[3vh] sb-in">
        <span style={{ fontSize: "5vw" }}>{data.emoji}</span>
        <div>
          <h1 className="font-black tracking-tight leading-none" style={{ fontSize: "4.4vw",
            background: "linear-gradient(95deg,#FFFFFF 0%,#BFE2FF 50%,#19E3FF 100%)",
            WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            {data.league}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <span className="rounded-full live-dot" style={{ width: "1.1vw", height: "1.1vw", background: "#FF3B30", boxShadow: "0 0 14px #FF3B30" }} />
            <span className="uppercase font-bold tracking-[0.3em] text-white/70" style={{ fontSize: "1.3vw" }}>Live Now</span>
          </div>
        </div>
      </div>

      {/* Matches */}
      <div className="relative w-full max-w-[78vw] flex flex-col gap-[2.2vh]">
        {matches.map((m, i) => (
          <MatchRow key={m.id || i} m={m} delay={i * 0.12} />
        ))}
      </div>

      <div className="absolute bottom-[3vh] text-white/40 font-bold tracking-[0.35em] uppercase" style={{ fontSize: "1.1vw" }}>
        SignHub · by AV Tech
      </div>
    </div>
  );
}

function Team({ t, align }) {
  return (
    <div className={`flex items-center gap-[1.5vw] flex-1 ${align === "right" ? "flex-row-reverse text-right" : ""}`}>
      {t.logo
        ? <img src={t.logo} alt="" className="object-contain" style={{ width: "5vw", height: "5vw" }} />
        : <div className="rounded-full bg-white/10 flex items-center justify-center font-black" style={{ width: "5vw", height: "5vw", fontSize: "1.8vw" }}>{(t.abbr || t.name || "?").slice(0, 3)}</div>}
      <span className="font-extrabold truncate" style={{ fontSize: "2.6vw", maxWidth: "22vw" }}>{t.name || t.abbr}</span>
    </div>
  );
}

function MatchRow({ m, delay }) {
  const live = m.state === "in";
  const liveLabel = [...new Set([m.clock, m.status].filter(Boolean))].join(" · ") || "LIVE";
  return (
    <div
      className="sb-in flex items-center rounded-2xl px-[3vw] py-[2.4vh]"
      style={{
        animationDelay: `${delay}s`,
        background: "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 100%)",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: live ? "0 0 50px rgba(10,132,255,0.35)" : "none",
      }}
    >
      <Team t={m.away} align="left" />
      <div className="flex flex-col items-center px-[2vw]" style={{ minWidth: "16vw" }}>
        <div className="flex items-center gap-[1.4vw] font-black tabular-nums leading-none" style={{ fontSize: "6vw" }}>
          <span>{m.away.score}</span>
          <span className="text-white/30" style={{ fontSize: "3vw" }}>–</span>
          <span>{m.home.score}</span>
        </div>
        <div
          className={`mt-[1.2vh] rounded-full px-[1.4vw] py-[0.5vh] font-bold uppercase tracking-wider whitespace-nowrap ${live ? "text-white" : "text-white/70"}`}
          style={{ fontSize: "1.3vw", background: live ? "#FF3B30" : "rgba(255,255,255,0.08)", boxShadow: live ? "0 0 18px rgba(255,59,48,0.6)" : "none" }}
        >
          {live ? liveLabel : m.status}
        </div>
      </div>
      <Team t={m.home} align="right" />
    </div>
  );
}

// Full-screen confetti + fireworks burst when a goal is scored.
export function GoalCelebration({ team }) {
  const confetti = useMemo(
    () => Array.from({ length: 90 }).map((_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.8,
      dur: 2.2 + Math.random() * 1.8,
      color: PALETTE[i % PALETTE.length],
      w: 8 + Math.random() * 10,
      h: 12 + Math.random() * 14,
    })),
    []
  );
  const fireworks = useMemo(
    () => Array.from({ length: 7 }).map((_, i) => ({
      left: 10 + Math.random() * 80,
      top: 10 + Math.random() * 50,
      delay: Math.random() * 0.9,
      size: 6 + Math.random() * 8,
      color: PALETTE[(i + 2) % PALETTE.length],
    })),
    []
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none overflow-hidden"
      data-testid="goal-celebration"
      style={{ background: "radial-gradient(ellipse at center, rgba(5,10,30,0.55) 0%, rgba(3,6,15,0.8) 100%)" }}>
      {confetti.map((c, i) => (
        <span key={i} className="confetti-piece"
          style={{ left: `${c.left}%`, background: c.color, width: c.w, height: c.h,
            animationDuration: `${c.dur}s`, animationDelay: `${c.delay}s`,
            borderRadius: i % 3 === 0 ? "50%" : "2px" }} />
      ))}
      {fireworks.map((f, i) => (
        <span key={`f${i}`} className="firework"
          style={{ left: `${f.left}%`, top: `${f.top}%`, width: f.size, height: f.size,
            color: f.color, animationDelay: `${f.delay}s` }} />
      ))}
      <div className="relative text-center goal-zoom">
        <h1 className="font-black leading-none" style={{ fontSize: "13vw",
          background: "linear-gradient(95deg,#FFD60A 0%,#FF9F0A 45%,#FF3B30 100%)",
          WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent",
          textShadow: "0 0 60px rgba(255,159,10,0.6)", filter: "drop-shadow(0 6px 24px rgba(0,0,0,0.5))" }}>
          GOOOAL!
        </h1>
        {team && (
          <p className="font-extrabold text-white mt-[1vh] tracking-wide" style={{ fontSize: "4vw", textShadow: "0 4px 18px rgba(0,0,0,0.6)" }}>
            {team}
          </p>
        )}
      </div>
    </div>
  );
}
